// Requêtes des écrans de proximité (lot L5-C1, M17, TanStack Query), clés sous « participation ». Types de
// packages/contrats/src/participation*.ts ; le serveur refait tous les contrôles (droits du domaine cible,
// périodes, états) : l'écran ne propose que des actes, il ne décide rien.
import type {
  BesoinQuartier,
  CompteRendu,
  ConsultationQuartier,
  CreationBesoinQuartier,
  CreationConsultationQuartier,
  CreationEngagementQuartier,
  CreationReunion,
  DesignationRepresentant,
  EngagementQuartier,
  FicheQuartier,
  OrientationBesoin,
  QualificationBesoin,
  Representant,
  ResumeQuartier,
  ReunionQuartier,
  SuiviEngagementQuartier,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface Liste<T> {
  elements: T[];
}

export const INVALIDER_PARTICIPATION: readonly (readonly unknown[])[] = [['participation'], ['indicateurs']];

const lire = <T,>(chemin: string) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal });
const envoyer = <T,>(chemin: string, corps: unknown = {}) => requete<T>(chemin, { methode: 'POST', corps });

export const useQuartiers = () => useQuery({ queryKey: ['participation', 'quartiers'], queryFn: lire<Liste<ResumeQuartier>>('/participation/quartiers'), retry: false });
export const useQuartier = (id: string | undefined) => useQuery({ queryKey: ['participation', 'quartier', id ?? ''], queryFn: lire<FicheQuartier>(`/participation/quartiers/${id}`), enabled: Boolean(id), retry: false });
export const useBesoins = () => useQuery({ queryKey: ['participation', 'besoins'], queryFn: lire<Liste<BesoinQuartier>>('/participation/besoins'), retry: false });
export const useBesoin = (id: string | undefined) => useQuery({ queryKey: ['participation', 'besoin', id ?? ''], queryFn: lire<BesoinQuartier>(`/participation/besoins/${id}`), enabled: Boolean(id), retry: false });

export const designer = (quartier: string, c: DesignationRepresentant) => envoyer<Representant>(`/participation/quartiers/${quartier}/representants`, c);
export const finirDesignation = (id: string, c: { fin: string; motif: string }) => envoyer<Representant>(`/participation/representants/${id}/fin`, c);
export const creerReunion = (quartier: string, c: CreationReunion) => envoyer<ReunionQuartier>(`/participation/quartiers/${quartier}/reunions`, c);
export const compteRendu = (id: string, c: CompteRendu) => envoyer<ReunionQuartier>(`/participation/reunions/${id}/compte-rendu`, c);
export const creerBesoin = (c: CreationBesoinQuartier) => envoyer<BesoinQuartier>('/participation/besoins', c);
export const qualifier = (id: string, c: QualificationBesoin) => envoyer<BesoinQuartier>(`/participation/besoins/${id}/qualification`, c);
export const rejeter = (id: string, motif: string) => envoyer<BesoinQuartier>(`/participation/besoins/${id}/rejet`, { motif });
export const orienter = (id: string, c: OrientationBesoin) => envoyer<BesoinQuartier>(`/participation/besoins/${id}/orientation`, c);
export const traiter = (id: string, observation: string) => envoyer<BesoinQuartier>(`/participation/besoins/${id}/traitement`, { observation });
export const creerConsultation = (c: CreationConsultationQuartier) => envoyer<ConsultationQuartier>('/participation/consultations', c);
export const publierConsultation = (id: string) => envoyer<ConsultationQuartier>(`/participation/consultations/${id}/publication`);
export const creerEngagement = (quartier: string, c: CreationEngagementQuartier) => envoyer<EngagementQuartier>(`/participation/quartiers/${quartier}/engagements`, c);
/** Couche existante des signalements localisés (module des interventions), lue seulement avec le droit de la voir. */
export interface CoucheSignalements {
  features: { id: string; geometry: { coordinates: [number, number] }; properties: { numero: string; categorie_libelle: string } }[];
}
export const useCoucheSignalements = (actif: boolean) =>
  useQuery({ queryKey: ['participation', 'couche-signalements'], queryFn: lire<CoucheSignalements>('/carte/signalements'), enabled: actif, retry: false });

export const suivreEngagement =(id: string, c: SuiviEngagementQuartier) => envoyer<EngagementQuartier>(`/participation/engagements/${id}/suivi`, c);
